import { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { DashboardLayout } from '../../layouts/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { ScrollArea } from '../../components/ui/scroll-area';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { Calendar } from '../../components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';
import { format } from 'date-fns';
import {
  getMedicines,
  createMedicine,
  stockIn,
  getBarangays,
} from '../../lib/api';
import {
  Pill,
  Plus,
  Search,
  AlertTriangle,
  Clock,
  Package,
  Calendar as CalendarIcon,
  TrendingUp,
} from 'lucide-react';

export default function MedicineInventory() {
  const { user, isMunicipalAdmin } = useAuth();
  const [medicines, setMedicines] = useState([]);
  const [barangays, setBarangays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [stockDialogOpen, setStockDialogOpen] = useState(false);
  const [selectedMedicine, setSelectedMedicine] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterBarangay, setFilterBarangay] = useState('all');
  const [showLowStock, setShowLowStock] = useState(false);
  const [showExpiring, setShowExpiring] = useState(false);

  // Form state
  const [formData, setFormData] = useState({
    barangay_id: '',
    name: '',
    quantity: '',
    expiry_date: new Date(),
    low_stock_threshold: '10',
    batch: '',
  });

  const [stockData, setStockData] = useState({
    quantity_received: '',
    source: '',
    delivery_date: new Date(),
    expiry_date: new Date(),
  });

  const [formLoading, setFormLoading] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [medicinesRes, barangaysRes] = await Promise.all([
        getMedicines(),
        getBarangays(),
      ]);
      setMedicines(medicinesRes.data);
      setBarangays(barangaysRes.data);

      if (!isMunicipalAdmin() && user?.barangay_id) {
        setFormData(prev => ({ ...prev, barangay_id: user.barangay_id }));
      }
    } catch (err) {
      console.error('Failed to load data:', err);
      toast.error('Failed to load medicine inventory');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormLoading(true);
    
    try {
      await createMedicine({
        ...formData,
        quantity: parseInt(formData.quantity),
        low_stock_threshold: parseInt(formData.low_stock_threshold),
        expiry_date: formData.expiry_date.toISOString(),
      });
      toast.success('Medicine added successfully');
      setDialogOpen(false);
      loadData();
      resetForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add medicine');
    } finally {
      setFormLoading(false);
    }
  };

  const handleStockIn = async (e) => {
    e.preventDefault();
    if (!selectedMedicine) return;
    setFormLoading(true);
    
    try {
      await stockIn(selectedMedicine.id, {
        medicine_id: selectedMedicine.id,
        quantity_received: parseInt(stockData.quantity_received),
        source: stockData.source,
        delivery_date: stockData.delivery_date.toISOString(),
        expiry_date: stockData.expiry_date.toISOString(),
      });
      toast.success('Stock added successfully');
      setStockDialogOpen(false);
      loadData();
      resetStockForm();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to add stock');
    } finally {
      setFormLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      barangay_id: !isMunicipalAdmin() ? user?.barangay_id || '' : '',
      name: '',
      quantity: '',
      expiry_date: new Date(),
      low_stock_threshold: '10',
      batch: '',
    });
  };

  const resetStockForm = () => {
    setStockData({
      quantity_received: '',
      source: '',
      delivery_date: new Date(),
      expiry_date: new Date(),
    });
    setSelectedMedicine(null);
  };

  const openStockDialog = (medicine) => {
    setSelectedMedicine(medicine);
    setStockDialogOpen(true);
  };

  const filteredMedicines = medicines.filter(medicine => {
    const matchesSearch = medicine.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesBarangay = filterBarangay === 'all' || medicine.barangay_id === filterBarangay;
    const isLowStock = medicine.quantity <= medicine.low_stock_threshold;
    const isExpiring = new Date(medicine.expiry_date) <= new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
    
    if (showLowStock && !isLowStock) return false;
    if (showExpiring && !isExpiring) return false;
    
    return matchesSearch && matchesBarangay;
  });

  const getBarangayName = (id) => barangays.find(b => b.id === id)?.name || 'Unknown';

  const stats = {
    totalItems: medicines.length,
    lowStock: medicines.filter(m => m.quantity <= m.low_stock_threshold).length,
    expiring: medicines.filter(m => new Date(m.expiry_date) <= new Date(Date.now() + 180 * 24 * 60 * 60 * 1000)).length,
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="medicine-inventory">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold font-['Public_Sans'] text-foreground">
              Medicine Inventory
            </h1>
            <p className="text-muted-foreground mt-1">
              Track and manage medicine stocks
            </p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
            <DialogTrigger asChild>
              <Button data-testid="add-medicine-btn">
                <Plus className="h-4 w-4 mr-2" />
                Add Medicine
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Add New Medicine</DialogTitle>
                <DialogDescription>
                  Add a new medicine to the inventory
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                {isMunicipalAdmin() && (
                  <div className="space-y-2">
                    <Label>Barangay *</Label>
                    <Select
                      value={formData.barangay_id}
                      onValueChange={(v) => setFormData({ ...formData, barangay_id: v })}
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select barangay" />
                      </SelectTrigger>
                      <SelectContent>
                        {barangays.map((b) => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <div className="space-y-2">
                  <Label>Medicine Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Paracetamol 500mg"
                    required
                    data-testid="medicine-name-input"
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Initial Quantity *</Label>
                    <Input
                      type="number"
                      value={formData.quantity}
                      onChange={(e) => setFormData({ ...formData, quantity: e.target.value })}
                      placeholder="Quantity"
                      required
                      min="0"
                      data-testid="medicine-quantity-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Low Stock Alert</Label>
                    <Input
                      type="number"
                      value={formData.low_stock_threshold}
                      onChange={(e) => setFormData({ ...formData, low_stock_threshold: e.target.value })}
                      placeholder="Threshold"
                      min="1"
                      data-testid="low-stock-input"
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Expiry Date *</Label>
                  <Popover>
                    <PopoverTrigger asChild>
                      <Button variant="outline" className="w-full justify-start text-left font-normal">
                        <CalendarIcon className="mr-2 h-4 w-4" />
                        {format(formData.expiry_date, 'PPP')}
                      </Button>
                    </PopoverTrigger>
                    <PopoverContent className="w-auto p-0">
                      <Calendar
                        mode="single"
                        selected={formData.expiry_date}
                        onSelect={(date) => setFormData({ ...formData, expiry_date: date || new Date() })}
                        initialFocus
                      />
                    </PopoverContent>
                  </Popover>
                </div>
                <div className="space-y-2">
                  <Label>Batch Number</Label>
                  <Input
                    value={formData.batch}
                    onChange={(e) => setFormData({ ...formData, batch: e.target.value })}
                    placeholder="Optional batch number"
                    data-testid="batch-input"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={formLoading} data-testid="submit-medicine-btn">
                    {formLoading ? 'Adding...' : 'Add Medicine'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Package className="h-8 w-8 text-primary" />
                <div>
                  <p className="text-2xl font-bold">{stats.totalItems}</p>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.lowStock > 0 ? 'border-amber-300 bg-amber-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <AlertTriangle className={`h-8 w-8 ${stats.lowStock > 0 ? 'text-amber-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.lowStock}</p>
                  <p className="text-sm text-muted-foreground">Low Stock Alerts</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className={stats.expiring > 0 ? 'border-orange-300 bg-orange-50/50' : ''}>
            <CardContent className="pt-4">
              <div className="flex items-center gap-3">
                <Clock className={`h-8 w-8 ${stats.expiring > 0 ? 'text-orange-600' : 'text-muted-foreground'}`} />
                <div>
                  <p className="text-2xl font-bold">{stats.expiring}</p>
                  <p className="text-sm text-muted-foreground">Expiring Soon (6mo)</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-4">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder="Search medicines..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9"
                  data-testid="search-medicine-input"
                />
              </div>
              {isMunicipalAdmin() && (
                <Select value={filterBarangay} onValueChange={setFilterBarangay}>
                  <SelectTrigger className="w-full md:w-[200px]">
                    <SelectValue placeholder="Filter by barangay" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Barangays</SelectItem>
                    {barangays.map((b) => (
                      <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <div className="flex gap-2">
                <Button
                  variant={showLowStock ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowLowStock(!showLowStock)}
                  data-testid="filter-low-stock"
                >
                  <AlertTriangle className="h-4 w-4 mr-1" />
                  Low Stock
                </Button>
                <Button
                  variant={showExpiring ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => setShowExpiring(!showExpiring)}
                  data-testid="filter-expiring"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Expiring
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Pill className="h-5 w-5 text-primary" />
              Inventory
            </CardTitle>
            <CardDescription>
              {filteredMedicines.length} medicines found
            </CardDescription>
          </CardHeader>
          <CardContent>
            <ScrollArea className="h-[400px]">
              <Table className="table-zebra">
                <TableHeader>
                  <TableRow>
                    <TableHead>Medicine Name</TableHead>
                    <TableHead>Quantity</TableHead>
                    <TableHead>Threshold</TableHead>
                    <TableHead>Expiry Date</TableHead>
                    <TableHead>Batch</TableHead>
                    {isMunicipalAdmin() && <TableHead>Barangay</TableHead>}
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMedicines.map((medicine) => {
                    const isLow = medicine.quantity <= medicine.low_stock_threshold;
                    const isExpiring = new Date(medicine.expiry_date) <= new Date(Date.now() + 180 * 24 * 60 * 60 * 1000);
                    
                    return (
                      <TableRow key={medicine.id} data-testid={`medicine-row-${medicine.id}`}>
                        <TableCell className="font-medium">{medicine.name}</TableCell>
                        <TableCell>
                          <Badge variant={isLow ? 'destructive' : 'secondary'}>
                            {medicine.quantity}
                          </Badge>
                        </TableCell>
                        <TableCell>{medicine.low_stock_threshold}</TableCell>
                        <TableCell>
                          <span className={isExpiring ? 'text-orange-600 font-medium' : ''}>
                            {new Date(medicine.expiry_date).toLocaleDateString()}
                          </span>
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {medicine.batch || '-'}
                        </TableCell>
                        {isMunicipalAdmin() && (
                          <TableCell className="text-sm">{getBarangayName(medicine.barangay_id)}</TableCell>
                        )}
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openStockDialog(medicine)}
                            data-testid={`stock-in-${medicine.id}`}
                          >
                            <TrendingUp className="h-3 w-3 mr-1" />
                            Stock In
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                  {filteredMedicines.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={isMunicipalAdmin() ? 7 : 6} className="text-center py-8 text-muted-foreground">
                        No medicines found
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </ScrollArea>
          </CardContent>
        </Card>

        {/* Stock In Dialog */}
        <Dialog open={stockDialogOpen} onOpenChange={(open) => { if (!open) resetStockForm(); setStockDialogOpen(open); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Stock In - {selectedMedicine?.name}</DialogTitle>
              <DialogDescription>
                Record new stock delivery
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleStockIn} className="space-y-4">
              <div className="space-y-2">
                <Label>Quantity Received *</Label>
                <Input
                  type="number"
                  value={stockData.quantity_received}
                  onChange={(e) => setStockData({ ...stockData, quantity_received: e.target.value })}
                  placeholder="Quantity"
                  required
                  min="1"
                  data-testid="stock-quantity-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Source *</Label>
                <Input
                  value={stockData.source}
                  onChange={(e) => setStockData({ ...stockData, source: e.target.value })}
                  placeholder="e.g., DOH, Municipal Health Office"
                  required
                  data-testid="stock-source-input"
                />
              </div>
              <div className="space-y-2">
                <Label>Delivery Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(stockData.delivery_date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={stockData.delivery_date}
                      onSelect={(date) => setStockData({ ...stockData, delivery_date: date || new Date() })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div className="space-y-2">
                <Label>Expiry Date *</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="outline" className="w-full justify-start text-left font-normal">
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      {format(stockData.expiry_date, 'PPP')}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={stockData.expiry_date}
                      onSelect={(date) => setStockData({ ...stockData, expiry_date: date || new Date() })}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <DialogFooter>
                <Button type="button" variant="outline" onClick={() => setStockDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" disabled={formLoading} data-testid="submit-stock-btn">
                  {formLoading ? 'Saving...' : 'Add Stock'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
